import { useMemo } from 'react'

type ContributionData = {
  date: string
  count: number
}

type Props = {
  data: ContributionData[]
}

export function ContributionGraph({ data }: Props) {
  const { weeks, months, totalCount } = useMemo(() => {
    const dataMap = new Map(data.map((d) => [d.date, d.count]))

    const year = new Date().getFullYear()
    const startDate = new Date(year, 0, 1)
    const endDate = new Date(year, 11, 31)

    // 週の開始日（日曜日）に調整
    const adjustedStart = new Date(startDate)
    adjustedStart.setDate(startDate.getDate() - startDate.getDay())

    const weeks: Array<Array<{ date: Date; count: number; isCurrentYear: boolean }>> = []
    let currentWeek: Array<{ date: Date; count: number; isCurrentYear: boolean }> = []
    let currentDate = new Date(adjustedStart)

    while (currentDate <= endDate || currentWeek.length > 0) {
      const dateStr = currentDate.toISOString().split('T')[0]
      const isCurrentYear = currentDate.getFullYear() === year

      currentWeek.push({
        date: new Date(currentDate),
        count: isCurrentYear ? (dataMap.get(dateStr) || 0) : 0,
        isCurrentYear,
      })

      if (currentWeek.length === 7) {
        weeks.push(currentWeek)
        currentWeek = []
      }

      currentDate.setDate(currentDate.getDate() + 1)

      if (currentDate > endDate && currentWeek.length === 0) {
        break
      }
    }

    if (currentWeek.length > 0) {
      weeks.push(currentWeek)
    }

    // 月のラベル位置を計算
    const months: Array<{ label: string; weekIndex: number }> = []
    let lastMonth = -1

    weeks.forEach((week, weekIndex) => {
      const firstDayOfWeek = week.find((d) => d.isCurrentYear)
      if (firstDayOfWeek) {
        const month = firstDayOfWeek.date.getMonth()
        if (month !== lastMonth) {
          months.push({
            label: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'][month],
            weekIndex,
          })
          lastMonth = month
        }
      }
    })

    const totalCount = data.reduce((sum, d) => sum + d.count, 0)

    return { weeks, months, totalCount }
  }, [data])

  const weekdays = ['日', '月', '火', '水', '木', '金', '土']

  return (
    <div className="space-y-4">
      <div className="-mx-6 overflow-hidden">
        <div className="overflow-x-auto px-6">
          <div className="inline-flex flex-col gap-1">
          {/* 月ラベル */}
          <div className="flex gap-[3px] pl-8">
            {months.map((month, index) => {
              const nextMonth = months[index + 1]
              const width = nextMonth
                ? (nextMonth.weekIndex - month.weekIndex) * 13
                : (weeks.length - month.weekIndex) * 13

              return (
                <div
                  key={month.label}
                  className="text-xs text-white/50"
                  style={{ width: `${width}px`, minWidth: `${width}px` }}
                >
                  {month.label}
                </div>
              )
            })}
          </div>

          {/* グラフ本体 */}
          <div className="flex gap-1">
            {/* 曜日ラベル */}
            <div className="flex flex-col gap-[3px]">
              {weekdays.map((day, index) => (
                <div
                  key={day}
                  className="flex h-[10px] w-6 items-center justify-end pr-1 text-[10px] text-white/50"
                >
                  {index % 2 === 1 ? day : ''}
                </div>
              ))}
            </div>

            {/* セル */}
            <div className="flex gap-[3px]">
              {weeks.map((week, weekIndex) => (
                <div key={weekIndex} className="flex flex-col gap-[3px]">
                  {week.map((day, dayIndex) => (
                    <div
                      key={dayIndex}
                      className={`h-[10px] w-[10px] rounded-sm ${getColorClass(day.count, day.isCurrentYear)}`}
                      title={day.isCurrentYear ? `${formatDate(day.date)}${day.count > 0 ? ' ✓' : ''}` : ''}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* 凡例 */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-white/60">
          {new Date().getFullYear()}年の記録: <span className="font-semibold text-white">{totalCount}日</span>
        </p>
        <div className="flex items-center gap-3 text-xs text-white/50">
          <div className="flex items-center gap-1">
            <div className="h-[10px] w-[10px] rounded-sm bg-white/10" />
            <span>未記録</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-[10px] w-[10px] rounded-sm bg-brand-500" />
            <span>記録済み</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function getColorClass(count: number, isCurrentYear: boolean): string {
  if (!isCurrentYear) return 'bg-transparent'
  return count > 0 ? 'bg-brand-500' : 'bg-white/10'
}

function formatDate(date: Date): string {
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`
}
